import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Users, ArrowLeftRight, Gift, Loader2, ChevronLeft, ChevronRight, Users2 } from "lucide-react";
import CustomerGroupsTab from "@/components/CustomerGroupsTab";
import { API } from "@/lib/api";
import { rewardDisplayLine } from "@/lib/rewardDisplay";


export default function VendorCustomersPage() {
  const { authHeaders, user } = useAuth();
  const navigate = useNavigate();
  const vid = user?.vendor_id;
  const [customers, setCustomers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [custPage, setCustPage] = useState(1);
  const [custTotal, setCustTotal] = useState(0);
  const [txPage, setTxPage] = useState(1);
  const [txTotal, setTxTotal] = useState(0);
  const [rwPage, setRwPage] = useState(1);
  const [rwTotal, setRwTotal] = useState(0);

  const load = useCallback(async () => {
    if (!vid) return;
    const h = authHeaders();
    const [cu, tx, rw] = await Promise.all([
      axios.get(`${API}/customers?client_id=${vid}&limit=20&skip=${(custPage - 1) * 20}`, { headers: h }),
      axios.get(`${API}/transactions?client_id=${vid}&limit=20&skip=${(txPage - 1) * 20}`, { headers: h }),
      axios.get(`${API}/rewards?limit=20&skip=${(rwPage - 1) * 20}`, { headers: h }),
    ]);
    setCustomers(cu.data.customers || []);
    setCustTotal(cu.data.total || 0);
    setTransactions(tx.data.transactions || []);
    setTxTotal(tx.data.total || 0);
    setRewards(rw.data.rewards || []);
    setRwTotal(rw.data.total || 0);
    setLoading(false);
  }, [vid, custPage, txPage, rwPage, authHeaders]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex items-center justify-center h-64 text-ink/40"><Loader2 size={20} className="animate-spin" /></div>;

  const Pager = ({ page, setPage, total, perPage = 20 }) => {
    const pages = Math.ceil(total / perPage);
    if (pages <= 1) return null;
    return (
      <div className="flex items-center justify-between pt-3">
        <p className="text-xs text-ink/40">{total} total</p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} className="h-7 w-7 p-0"><ChevronLeft size={14} /></Button>
          <span className="text-xs text-ink/60">{page} / {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} className="h-7 w-7 p-0"><ChevronRight size={14} /></Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-5" data-testid="vendor-customers-page">
      <div>
        <h1 className="text-2xl font-bold text-ink tracking-tight">Customers</h1>
        <p className="text-sm text-ink/40 mt-0.5">Manage your customers, transactions and rewards</p>
      </div>

      <Tabs defaultValue="customers">
        <TabsList>
          <TabsTrigger value="customers" data-testid="tab-customers"><Users size={13} className="mr-1.5" /> Customers ({custTotal})</TabsTrigger>
          <TabsTrigger value="transactions" data-testid="tab-transactions"><ArrowLeftRight size={13} className="mr-1.5" /> Transactions</TabsTrigger>
          <TabsTrigger value="rewards" data-testid="tab-rewards"><Gift size={13} className="mr-1.5" /> Rewards</TabsTrigger>
          <TabsTrigger value="groups" data-testid="tab-groups"><Users2 size={13} className="mr-1.5" /> Groups</TabsTrigger>
        </TabsList>

        <TabsContent value="customers" className="mt-4">
          <Card className="bg-white border-0 shadow-sm rounded-2xl">
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="text-xs uppercase tracking-widest">Name</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Email / Mobile</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Points</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Tier</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Joined</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {customers.map((c) => (
                    <TableRow key={c.id} className="cursor-pointer hover:bg-purple-50" onClick={() => navigate(`/customers/${c.id}`)}>
                      <TableCell className="font-medium text-ink">{c.name || "—"}</TableCell>
                      <TableCell className="text-sm text-ink/60">{c.email || c.mobile_number || "—"}</TableCell>
                      <TableCell className="font-semibold text-ink">{c.point_balance?.toLocaleString()}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{c.tier || "—"}</Badge></TableCell>
                      <TableCell className="text-xs text-ink/40">{c.created_at?.slice(0, 10)}</TableCell>
                    </TableRow>
                  ))}
                  {customers.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-ink/40 py-8">No customers yet</TableCell></TableRow>}
                </TableBody>
              </Table>
              <div className="px-4 pb-3"><Pager page={custPage} setPage={setCustPage} total={custTotal} /></div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="transactions" className="mt-4">
          <Card className="bg-white border-0 shadow-sm rounded-2xl">
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="text-xs uppercase tracking-widest">Customer</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Type</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Points</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Amount</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Date</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {transactions.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell className="text-sm text-ink">{t.customer_name || t.customer_id?.slice(0, 10) || "—"}</TableCell>
                      <TableCell><Badge variant="outline" className={`text-xs ${t.type === "earn" ? "bg-purple-100 text-success" : "bg-purple-100 text-ink"}`}>{t.type}</Badge></TableCell>
                      <TableCell className="font-semibold text-ink">{t.points > 0 ? "+" : ""}{t.points}</TableCell>
                      <TableCell className="text-sm text-ink/60">{t.amount_gbp ? `£${t.amount_gbp}` : "—"}</TableCell>
                      <TableCell className="text-xs text-ink/40">{t.created_at?.slice(0, 10)}</TableCell>
                    </TableRow>
                  ))}
                  {transactions.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-ink/40 py-8">No transactions yet</TableCell></TableRow>}
                </TableBody>
              </Table>
              <div className="px-4 pb-3"><Pager page={txPage} setPage={setTxPage} total={txTotal} /></div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rewards" className="mt-4">
          <Card className="bg-white border-0 shadow-sm rounded-2xl">
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="text-xs uppercase tracking-widest">Customer</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Description</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Issued</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {rewards.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="text-sm text-ink">{r.customer_name || r.customer_id?.slice(0, 10) || "—"}</TableCell>
                      <TableCell><Badge variant="outline" className={`text-xs ${r.status === "issued" ? "bg-purple-100 text-success" : r.status === "redeemed" ? "bg-purple-100 text-ink" : "bg-danger/10 text-danger"}`}>{r.status}</Badge></TableCell>
                      <TableCell className="text-sm text-ink/60">{rewardDisplayLine(r) || r.label || r.reward_description || r.description || "Reward"}</TableCell>
                      <TableCell className="text-xs text-ink/40">{r.issued_at?.slice(0, 10)}</TableCell>
                    </TableRow>
                  ))}
                  {rewards.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-ink/40 py-8">No rewards yet</TableCell></TableRow>}
                </TableBody>
              </Table>
              <div className="px-4 pb-3"><Pager page={rwPage} setPage={setRwPage} total={rwTotal} /></div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="groups" className="mt-4">
          <CustomerGroupsTab vendorId={vid} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
